// ═══════════════════════════════════════════════════════════
// OBSTACLE MANAGER - Random Hazards
// ═══════════════════════════════════════════════════════════

import { Vector2, Bounds, Obstacle } from './types.js';
import { Utils } from './Utils.js';
import { Theme, applyNeonGlow, clearNeonGlow } from './Theme.js';

export class ObstacleManager {
    private obstacles: Obstacle[] = [];
    private bounds: Bounds;
    private obstacleSize: number = 30;
    private maxObstacles: number = 5;

    constructor(bounds: Bounds) {
        this.bounds = bounds;
    }

    generateObstacles(count: number): void {
        this.obstacles = [];
        const padding = 100; // Keep obstacles away from edges and center

        for (let i = 0; i < count; i++) {
            let position: Vector2;
            let attempts = 0;
            const maxAttempts = 50;

            // Try to find a valid position that doesn't overlap with existing obstacles
            do {
                position = {
                    x: Utils.random(padding, this.bounds.width - padding),
                    y: Utils.random(padding, this.bounds.height - padding)
                };
                attempts++;
            } while (this.isTooCloseToCenter(position) && attempts < maxAttempts);

            // Skip if we couldn't find a good position
            if (attempts >= maxAttempts) continue;

            // Random velocity for movement
            const speed = Utils.random(30, 80); // pixels per second
            const angle = Utils.random(0, Math.PI * 2);
            const velocity: Vector2 = {
                x: Math.cos(angle) * speed,
                y: Math.sin(angle) * speed
            };

            this.obstacles.push({
                position,
                velocity,
                size: this.obstacleSize,
                rotation: Utils.random(0, Math.PI * 2),
                pulsePhase: Utils.random(0, Math.PI * 2)
            });
        }
    }

    private isTooCloseToCenter(position: Vector2): boolean {
        const centerX = this.bounds.width / 2;
        const centerY = this.bounds.height / 2;
        const distance = Math.sqrt(
            Math.pow(position.x - centerX, 2) + Math.pow(position.y - centerY, 2)
        );
        return distance < 150; // Keep obstacles away from spawn point
    }

    update(deltaTime: number): void {
        const padding = this.obstacleSize / 2;

        // Update animations and movement
        for (const obstacle of this.obstacles) {
            // Update rotation and pulse animations (enhanced speeds from theme)
            obstacle.rotation += deltaTime * Theme.obstacles.spinSpeed;
            obstacle.pulsePhase += deltaTime * Theme.obstacles.pulseSpeed;

            // Update position based on velocity
            obstacle.position.x += obstacle.velocity.x * deltaTime;
            obstacle.position.y += obstacle.velocity.y * deltaTime;

            // Bounce off boundaries
            if (obstacle.position.x - padding < 0 || obstacle.position.x + padding > this.bounds.width) {
                obstacle.velocity.x *= -1;
                // Clamp position to keep obstacle inside bounds
                obstacle.position.x = Math.max(padding, Math.min(this.bounds.width - padding, obstacle.position.x));
            }

            if (obstacle.position.y - padding < 0 || obstacle.position.y + padding > this.bounds.height) {
                obstacle.velocity.y *= -1;
                // Clamp position to keep obstacle inside bounds
                obstacle.position.y = Math.max(padding, Math.min(this.bounds.height - padding, obstacle.position.y));
            }
        }
    }

    checkCollision(ballPosition: Vector2, ballSize: number): boolean {
        const ballRadius = ballSize / 2;

        for (const obstacle of this.obstacles) {
            const distance = Math.sqrt(
                Math.pow(ballPosition.x - obstacle.position.x, 2) +
                Math.pow(ballPosition.y - obstacle.position.y, 2)
            );

            // Simple circle-circle collision
            if (distance < ballRadius + obstacle.size / 2) {
                return true;
            }
        }

        return false;
    }

    getObstacles(): Obstacle[] {
        return this.obstacles;
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const obstacle of this.obstacles) {
            this.renderObstacle(ctx, obstacle);
        }
    }

    private renderObstacle(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
        const offsetY = this.bounds.offsetY || 0;
        ctx.save();
        ctx.translate(obstacle.position.x, obstacle.position.y + offsetY);

        // Pulsing scale effect (enhanced)
        const pulseScale = 1 + Math.sin(obstacle.pulsePhase) * 0.15;
        ctx.scale(pulseScale, pulseScale);

        const size = Theme.obstacles.coreSize;

        // Draw multiple neon glow layers
        this.renderDroneGlow(ctx, size);

        // Rotate for spinning drone effect
        ctx.rotate(obstacle.rotation);

        // Draw cyber drone body
        this.renderDroneBody(ctx, size);

        // Draw warning eye
        this.renderDroneEye(ctx, size, obstacle.pulsePhase);

        // Draw exhaust trails
        this.renderDroneExhaust(ctx, size, obstacle.pulsePhase);

        ctx.restore();
    }

    /**
     * Render neon glow around cyber drone
     */
    private renderDroneGlow(ctx: CanvasRenderingContext2D, size: number): void {
        // Multiple glow layers for cyber effect
        for (let i = 0; i < 3; i++) {
            const glowRadius = size * (1.5 + i * 0.5);
            const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);

            const color = Theme.obstacles.bodyColor;
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);

            const alpha = 0.4 - i * 0.1;
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(-glowRadius, -glowRadius, glowRadius * 2, glowRadius * 2);
        }
    }

    /**
     * Render cyber drone body (octagon shape)
     */
    private renderDroneBody(ctx: CanvasRenderingContext2D, size: number): void {
        const radius = size / 2;
        const sides = 8; // Octagon

        // Draw octagon body
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const angle = (i / sides) * Math.PI * 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }
        ctx.closePath();

        // Gradient fill
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
        gradient.addColorStop(0, Theme.obstacles.bodyColor);
        gradient.addColorStop(0.7, Theme.colors.darkPurple);
        gradient.addColorStop(1, Theme.obstacles.bodyColor);

        ctx.fillStyle = gradient;
        ctx.fill();

        // Glowing outline
        applyNeonGlow(ctx, Theme.obstacles.bodyColor, Theme.glow.obstacleGlow);
        ctx.strokeStyle = Theme.obstacles.bodyColor;
        ctx.lineWidth = 2;
        ctx.stroke();
        clearNeonGlow(ctx);

        // Draw inner tech details (lines)
        ctx.strokeStyle = Theme.obstacles.exhaustColor;
        ctx.lineWidth = 1;
        applyNeonGlow(ctx, Theme.obstacles.exhaustColor, 10);

        // Horizontal line
        ctx.beginPath();
        ctx.moveTo(-radius * 0.6, 0);
        ctx.lineTo(radius * 0.6, 0);
        ctx.stroke();

        // Vertical line
        ctx.beginPath();
        ctx.moveTo(0, -radius * 0.6);
        ctx.lineTo(0, radius * 0.6);
        ctx.stroke();

        clearNeonGlow(ctx);
    }

    /**
     * Render warning eye in center
     */
    private renderDroneEye(ctx: CanvasRenderingContext2D, size: number, pulsePhase: number): void {
        const eyeSize = Theme.obstacles.eyeSize;
        const pulseIntensity = 0.7 + Math.sin(pulsePhase) * 0.3;

        // Eye glow
        const glowGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, eyeSize * 2);
        glowGradient.addColorStop(0, `rgba(255, 0, 0, ${pulseIntensity * 0.8})`);
        glowGradient.addColorStop(0.5, `rgba(255, 0, 0, ${pulseIntensity * 0.4})`);
        glowGradient.addColorStop(1, 'rgba(255, 0, 0, 0)');

        ctx.fillStyle = glowGradient;
        ctx.fillRect(-eyeSize * 2, -eyeSize * 2, eyeSize * 4, eyeSize * 4);

        // Eye core
        applyNeonGlow(ctx, Theme.obstacles.eyeColor, 15);
        ctx.fillStyle = Theme.obstacles.eyeColor;
        ctx.beginPath();
        ctx.arc(0, 0, eyeSize, 0, Math.PI * 2);
        ctx.fill();
        clearNeonGlow(ctx);

        // Eye highlight
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.beginPath();
        ctx.arc(-eyeSize * 0.3, -eyeSize * 0.3, eyeSize * 0.4, 0, Math.PI * 2);
        ctx.fill();
    }

    /**
     * Render exhaust trails
     */
    private renderDroneExhaust(ctx: CanvasRenderingContext2D, size: number, pulsePhase: number): void {
        const exhaustLength = Theme.obstacles.exhaustLength;
        const exhaustIntensity = 0.6 + Math.sin(pulsePhase * 2) * 0.4;

        // Four exhaust ports at cardinal directions
        const directions = [
            { angle: 0, x: size / 2, y: 0 },           // Right
            { angle: Math.PI / 2, x: 0, y: size / 2 }, // Bottom
            { angle: Math.PI, x: -size / 2, y: 0 },    // Left
            { angle: -Math.PI / 2, x: 0, y: -size / 2 } // Top
        ];

        for (const dir of directions) {
            ctx.save();

            // Draw exhaust glow
            const gradient = ctx.createLinearGradient(
                dir.x,
                dir.y,
                dir.x + Math.cos(dir.angle) * exhaustLength,
                dir.y + Math.sin(dir.angle) * exhaustLength
            );

            const exhaustColor = Theme.obstacles.exhaustColor;
            const r = parseInt(exhaustColor.slice(1, 3), 16);
            const g = parseInt(exhaustColor.slice(3, 5), 16);
            const b = parseInt(exhaustColor.slice(5, 7), 16);

            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${exhaustIntensity})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${exhaustIntensity * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;

            // Draw exhaust trail
            const trailWidth = 6;
            ctx.fillRect(
                dir.x - trailWidth / 2,
                dir.y - trailWidth / 2,
                Math.cos(dir.angle) * exhaustLength + trailWidth,
                Math.sin(dir.angle) * exhaustLength + trailWidth
            );

            ctx.restore();
        }
    }

    setDifficulty(level: number): void {
        // Increase obstacle count with difficulty (max 8)
        this.maxObstacles = Math.min(5 + Math.floor(level / 3), 8);
    }

    regenerateWithDifficulty(level: number): void {
        this.setDifficulty(level);
        // Start with 1 obstacle at level 1, add more gradually
        // Levels 1-2: 1 obstacle, Levels 3-5: 2 obstacles, Levels 6-8: 3 obstacles, etc.
        const obstacleCount = Math.min(Math.floor(level / 3) + 1, this.maxObstacles);
        this.generateObstacles(obstacleCount);
    }

    reset(): void {
        this.obstacles = [];
        this.maxObstacles = 5;
    }
}
